/**
 * src/pages/reports/_reportFormat.js — captions and range labels for the CEO
 * AI report. Card P214, from Daniel's live read of v2.1.54 (1 Nov, ~00:55 ET).
 *
 * Two small jobs, in one module because both are shared by the dialog chrome
 * (AiPipelineReport) and the rendered brief (CeoReportView), and a second
 * copy of either is how the two come to disagree on screen.
 */

/* ═══════════════════════════════════════════════════════════════════════════
 * 1 · Reason codes → captions
 *
 * ⛔⛔ WHAT CARLOS ACTUALLY READ UNDER THREE KPI TILES:
 *   "snapshot_of_now_has_no_prior_w…"
 *   "source_column_not_populated_s…"
 *   "a snapshot of what is unpaid now — it has no prior window"
 *
 * The first two are INTERNAL IDENTIFIERS from routes/_ceo_metric.js, printed
 * verbatim and then truncated by the tile's width, so they were not even
 * complete identifiers. The third was prose I wrote into the receivables tile
 * in P201 — different in form, same mistake: the field says WHY a delta is
 * missing, and the tile rendered the raw value of that field.
 *
 * ⚠⚠ THE CODES ARE DUPLICATED HERE, AND THAT IS FORCED, NOT CHOSEN.
 * create-react-app's module scope forbids src/ importing routes/, so the
 * client cannot read NO_PREVIOUS directly. A parity test requires both and
 * asserts this table's keys are exactly that object's values — so a code
 * added, renamed or removed on the server fails in the PR that does it,
 * rather than appearing on a CEO's screen as an identifier.
 *
 * ⛔ AN UNKNOWN CODE RENDERS NOTHING. Never the code itself: a caption is a
 * courtesy, and a missing courtesy is a blank line while a leaked identifier
 * is a bug report from the person who was supposed to be reading figures.
 * ═══════════════════════════════════════════════════════════════════════════ */
export const REASON_CAPTIONS = Object.freeze({
  all_time_range_has_no_prior_window:
    'No prior period to compare',
  snapshot_of_now_has_no_prior_window:
    'A snapshot of right now — there is nothing to compare it against',
  source_column_not_populated_so_a_prior_window_cannot_be_measured:
    'The date this is measured from is not recorded, so a prior period cannot be worked out',
  /*
   * ⛔⛔ Card P237 — THE SUPPRESSION CODES. Added here because an unknown code
   * renders NOTHING, and a suppressed figure with no caption is exactly the
   * "reads as flat" failure the suppression exists to prevent — in the PDF,
   * where nobody can hover to find out why the arrow is missing.
   *
   * ⚠ prior_window_starts_before_this_source_has_data deliberately has NO
   * date in it: the server's previousNote carries the measured coverage start
   * ("…starts Jul 16, 2026") and is preferred over this fallback. A date
   * hard-coded here would go stale the moment coverage changes and would then
   * disagree with the server about when the data begins.
   */
  prior_window_starts_before_this_source_has_data:
    'Not enough history to compare this period',
  prior_window_has_no_activity_to_compare_against: 'New',
  prior_window_base_too_small_for_a_percentage:
    'Too small to compare',
  period_too_young_to_compare: 'Too early to compare',
  no_prior_window_exists_for_this_range: 'No comparable period',
});

/**
 * The caption for a suppressed comparison, preferring the SERVER's sentence.
 *
 * ⛔⛔ previousNote WINS OVER THE CODE MAP. The server measured the coverage
 * start; this file cannot, and a date written here would disagree with the
 * server the moment coverage moves. The map is the fallback for an older
 * payload that carries a code and no note.
 *
 * ⚠ The note is still constrained to the KNOWN shapes — it is rendered only
 * when the code is one this file recognises. That keeps the "free prose is
 * rejected" rule: the server may choose the words, but not whether a sentence
 * appears at all.
 */
export function comparisonCaption(metric) {
  const m = metric || {};
  const code = typeof m.previousReason === 'string' ? m.previousReason : null;
  if (!code || !REASON_CAPTIONS[code]) return null;
  const note = typeof m.previousNote === 'string' ? m.previousNote.trim() : '';
  return note || REASON_CAPTIONS[code];
}

/**
 * A human caption for a reason code, or null.
 *
 * ⚠ Only a KNOWN CODE produces a caption. Free prose is rejected too — it was
 * the third thing on screen, and a tile is not where a sentence belongs.
 */
export function captionForReason(code) {
  if (typeof code !== 'string') return null;
  return REASON_CAPTIONS[code] || null;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * 2 · The period label
 *
 * ⛔⛔ THE DIALOG SAID "Month" WHILE THE BRIEF SAID "Custom range". Two
 * labels, one window, on one screen — and neither told the reader which days
 * the figures covered. The dialog showed the dashboard's preset name; the
 * header showed the SERVER's label, which is "Custom range" for any request
 * carrying an explicit from/to, whatever the user picked.
 *
 * ⚠ So the label is derived from the DATES wherever there are dates, and
 * falls back to a name only when there are none. A name can disagree with a
 * window; two dates cannot disagree with themselves.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Generic server-side labels that tell a reader nothing about which days. */
const UNINFORMATIVE = new Set(['custom range', 'custom', 'range', '']);

const DAY = { day: 'numeric', month: 'short', year: 'numeric' };

/**
 * Format a range as a readable label.
 *
 * @param {object|null} range  { from, to, label } — either the dashboard's
 *   currentRange or the payload's own period block. Both carry those keys.
 * @returns {string} e.g. "1 Nov 2026", "1 Oct 2026 → 31 Oct 2026", "Month".
 */
export function formatRangeLabel(range) {
  if (!range) return '';
  const from = range.from ? new Date(range.from) : null;
  const to = range.to ? new Date(range.to) : null;
  const ok = (d) => d instanceof Date && !Number.isNaN(d.getTime());

  if (ok(from) && ok(to)) {
    const a = from.toLocaleDateString(undefined, DAY);
    const b = to.toLocaleDateString(undefined, DAY);
    /*
     * ⚠ A SINGLE DAY IS WRITTEN ONCE. Daniel's window was today-only, and
     * "1 Nov 2026 → 1 Nov 2026" reads like a range that failed to be set
     * rather than a deliberate one-day view.
     */
    return a === b ? a : `${a} → ${b}`;
  }
  if (ok(from)) return `from ${from.toLocaleDateString(undefined, DAY)}`;

  const label = typeof range.label === 'string' ? range.label.trim() : '';
  /* ⛔ "Custom range" with no dates is worse than nothing — it claims a
     window was chosen and declines to say which. */
  return UNINFORMATIVE.has(label.toLowerCase()) ? '' : label;
}

/**
 * "generated 1 Nov 2026, 12:54" — no seconds.
 *
 * ⚠ Seconds on a report header imply a precision the figures do not have:
 * the payload is built from queries that take seconds to run, so the stamp is
 * already approximate at that resolution. It also made the line longer than
 * the range it sat beside.
 */
export function formatGeneratedAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/* ═══════════════════════════════════════════════════════════════════════════
 * 3 · Inline emphasis, and the asterisks that leaked
 *
 * ⛔⛔ THE HEADLINE RENDERED ITS OWN MARKDOWN SOURCE. On v2.1.54 it read
 * literally: "**4,168** open opps / **$2.21M** … **1 new lead**". The bullets
 * were fine — CeoReportView ran them through an emphasiser — and headline and
 * summary were dropped straight into a <p>. The prompt asks for figures in
 * bold, so the model was doing as it was told; nothing rendered it.
 *
 * ⚠ BOTH HALVES, as the card asks: the schema and prompt now say plain text
 * in string fields, AND this renders a bold-only subset for the output of a
 * model that keeps the habit anyway. Belt and braces, because one of them is
 * an instruction to a language model.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⛔ A LAST PASS FOR STRAY ASTERISKS. `**bold**` is handled by the emphasiser;
 * this catches what it cannot pair — an unclosed `**`, a single `*`, an
 * `***emphatic***`. Those are markdown the reader did not ask for either, and
 * leaving them is the same defect one character narrower.
 *
 * ⚠ It runs on text that has ALREADY had its pairs consumed, so it cannot eat
 * a legitimate bold marker. A bare `*` in a CEO brief is never multiplication
 * — the payload's figures are formatted strings.
 */
export function stripStrayMarkdown(text) {
  return String(text == null ? '' : text).replace(/\*+/g, '');
}
